import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ConfigProvider } from 'antd';
import { antdTheme } from './antd-config';
import 'antd/dist/reset.css';
import { getToken } from './getSession';
import Leads from './pages/Lead/Leads/Leads';
import NotificationPage from './components/common/Notification/Notification';
import { NavigationProvider } from './context/NavigationContext';
import GlobalRouteGuard from './components/common/GlobalRouteGuard';

// Layout
import MainLayout from './components/layout/MainLayout';

// Providers
import Providers from './components/common/Template_Layouts/Providers';

// SSO
import { SSOProvider } from './lib/sso/ssoContext';
import ssoConfig from './config/ssoConfig';
import { Kit19SSOClient } from './lib/sso/ssoClient';
import SSOCallback from './pages/SSOCallback';

// MQTT
import { autoInitMqtt, onLogoutEvent } from './services/mqttLogoutService';

// Pages
import Enquiries from './pages/Enquiries/Enquiries/Enquiries';
import Login from './pages/Login';
import PhysicalAppointmentModule from './pages/PhysicalAppointment/Components/PhysicalAppointmentModule';
import Dashboard from './pages/Dashboard';
import AccessDenied from './components/common/AccessDenied/AccessDenied';
import LeadActivityModule from './pages/LeadActivity/LeadActivityModule';

import PipelinePage from './pages/PipelinePage';
import ExampleUsage from './components/PopUpModal/ExampleUsage';
import TaskModule from './pages/Task/Components/TaskModule';
import AppointmentModule from './pages/Appointment/Components/AppointmentModule';
import FollowUpModule from './pages/FollowUp/Components/FollowUpModule';
import PipelineDealModule from './pages/Pipeline/components/PipelineDealModule';
import PipelineHistory from './pages/Pipeline/components/PipelineHistory';
import QuotationsModule from './pages/Quotations/Components/QuotationsModule';
import SegmentationModule from './pages/Segmentation/SegmentationModule';
import CallListModuleV2 from './pages/CallList/CallListModuleV2';
import InvoiceModule from './pages/Invoice/Components/InvoiceModule'
import RevenueModule from './pages/Revenue/RevenueModule';
import MergeduplicateModule from './pages/MergeDuplicate/components/MergeduplicateModule';
import CreditNoteModuleWrapper from './pages/CreditNote/CreditNoteModuleWrapper';
import LeadExportModule from './pages/LeadExport/LeadExportModule';
import CustomerLedger from './pages/Invoice/CustomerLedger/CustomerLedger';
import CustomEventModule from './pages/CustomEvent/CustomEventModule';

import Conversions from './pages/Conversion/Conversions';

// Lazy load Invoice components to break circular dependencies
const InvoiceAddWrapper = lazy(() => import('./pages/Invoice/Components/InvoiceAddWrapper'));
const InvoiceEditWrapper = lazy(() => import('./pages/Invoice/Components/InvoiceEditWrapper'));
const InvoicePreviewWrapper = lazy(() => import('./pages/Invoice/Components/InvoicePreviewWrapper'));
const InvoiceListSettings = lazy(() => import('./pages/Master_Settings/InvoiceList_Settingds'));
const QuotationListSettings = lazy(() => import('./pages/Master_Settings/QuotationList_Settings'));
const TaxSettings = lazy(() => import('./pages/Master_Settings/TaxSettings'));
const CollaboratorType = lazy(() => import('./pages/Master_Settings/CollaboratorType'));
const CollaboratorTeam = lazy(() => import('./pages/Master_Settings/CollaboratorTeam'));
const PaymentMethodSettings = lazy(() => import('./pages/Master_Settings/PaymentMethodSettings'));
const FollowUpSettings = lazy(() => import('./pages/Master_Settings/FollowUpSettings'));
const PipelineSettings = lazy(() => import('./pages/Master_Settings/PipelineSettings'));
const TaskSetting = lazy(() => import('./pages/Master_Settings/TaskSetting'));
const CallDialerSetting = lazy(() => import('./pages/Master_Settings/CallDialerSetting'));
const CreditNoteSettingsPage = lazy(() => import('./pages/CreditNote/setting/CreditNoteSettings'));
const CustomButtonSettingPage = lazy(() => import('./pages/settings/CustomButtonSetting/CustomButtonSetting'));
const EntityApprovalSetting = lazy(() => import('./pages/Master_Settings/EntityApprovalSetting'));
const UserHierarchySettings = lazy(() => import('./pages/Master_Settings/UserHierarchySettings/UserHierarchySettings'));
const LeadAssignmentSettings = lazy(() => import('./pages/Master_Settings/LeadAssignmentSettings'));
const UserSourceSettings = lazy(() => import('./pages/Master_Settings/UserSourceSettings'));
const UserCampaignSettings = lazy(() => import('./pages/Master_Settings/UserCampaignSettings'));
const UserMediumSettings = lazy(() => import('./pages/Master_Settings/UserMediumSettings'));
const UserMailSettings = lazy(() => import('./pages/Master_Settings/UserMailSettings'));
const ProductSearchCriteria = lazy(() => import('./pages/Master_Settings/ProductSearchCriteria'));
const FieldMasking = lazy(() => import('./pages/Master_Settings/FieldMasking'));
const EnquiryReport = lazy(() => import('./pages/Report/EnquiryReport'));
const WhatsappSubscriptionReport = lazy(() => import('./pages/Report/WhatsappSubscriptionReport'));
const EmailSubscriptionReport = lazy(() => import('./pages/Report/EmailSubscriptionReport'));
const FollowUpReport = lazy(() => import('./pages/Report/FollowUpReport'));
const UsageReport = lazy(() => import('./pages/Report/UsageReport'));
const ProductImportStatus = lazy(() => import('./pages/Product/ProductImportStatus'));
const MassOperationStatusReport = lazy(() => import('./pages/Report/MassOperationStatusReport'));
const SalesTarget = lazy(() => import('./pages/Report/SalesTarget/SalesTarget'));
const LeadReport = lazy(() => import('./pages/Report/LeadReport/LeadReport'));
const CallReportDashboard = lazy(() => import('./pages/Report/CallReport/CallReportDashboard'));
const CallDetail = lazy(() => import('./pages/Report/CallReport/CallDetail'));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const SSO_ENABLED = process.env.REACT_APP_SSO_ENABLED === 'true';

// Public route wrapper
const PublicRoute = ({ children }) => {
  const isAuthenticated = getToken() !== null;
  return isAuthenticated ? <Navigate to="/" replace /> : children;
};

// 🔐 Protected route - supports both SSO and legacy auth
const ProtectedRoute = () => {
  const [allowed, setAllowed] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const authType = localStorage.getItem('kit_auth_type');

      if (SSO_ENABLED && (authType === 'sso' || !authType)) {
        // SSO mode or fresh session: validate token with SSO server
        const token = getToken();
        if (!token) {
          setAllowed(false);
          return;
        }

        try {
          const ssoClient = new Kit19SSOClient(ssoConfig);
          const validation = await ssoClient.validateToken(token);
          if (validation.valid) {
            setAllowed(true);
          } else {
            console.warn('SSO token invalid, clearing session');
            ssoClient.clearSession(); // Clear stale tokens to avoid blinking loop
            setAllowed(false);
          }
        } catch (error) {
          console.error('SSO token validation error:', error);
          const ssoClient = new Kit19SSOClient(ssoConfig);
          ssoClient.clearSession(); // Clear tokens on error too
          setAllowed(false);
        }
      } else {
        // Legacy mode or Local login: basic check for Token
        const token = getToken();
        setAllowed(!!token);
      }
    };

    checkAuth();
  }, [location.pathname]);

  // Loading state
  if (allowed === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #2a9629',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!allowed) {
    // Redirect to local login page so they see the choice (Image 2)
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

const ProtectedLayout = () => (
  <NavigationProvider>
    <GlobalRouteGuard>
      <MainLayout />
    </GlobalRouteGuard>
  </NavigationProvider>
);

const AppRoutes = () => {
  const location = useLocation();

  // Initialize MQTT connection for cross-app logout (runs when route changes and user is authenticated)
  useEffect(() => {
    const isAuthenticated = !!(localStorage.getItem('kit_userId') || localStorage.getItem('userId') || localStorage.getItem('Token') || localStorage.getItem('kit_sso_token'));

    // Do not initialize on login or callback pages where session is still being set up
    if (location.pathname === '/login' || location.pathname === '/callback' || !isAuthenticated) {
      return undefined;
    }

    autoInitMqtt(); // connect + subscribe to kit19/logout/{deviceId}
    const unsubscribe = onLogoutEvent(() => {
      // Another Kit19 app signed this device out — clear our session and go to login.
      console.log('[Kit19Sales] Cross-app logout received, navigating to login...');
      const deviceId = localStorage.getItem('kit19_device_id');
      const ssoConfig = localStorage.getItem('kit19_sso_config');
      localStorage.clear();
      if (deviceId) localStorage.setItem('kit19_device_id', deviceId);
      if (ssoConfig) localStorage.setItem('kit19_sso_config', ssoConfig);
      sessionStorage.clear();
      window.location.href = window.location.origin + '/login';
    });

    return () => { if (unsubscribe) unsubscribe(); };
  }, [location.pathname]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* SSO Callback Route */}
      <Route path="/callback" element={<SSOCallback />} />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<ProtectedLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="access-denied" element={<AccessDenied />} />
          <Route path="enquiries" element={<Enquiries />} />
          <Route path="leads" element={<Leads />} />
          <Route path="tasks" element={<TaskModule />} ></Route>
          <Route path="exampleusage" element={<ExampleUsage />} />
          <Route path="conversions" element={<Conversions />} />
          <Route path="segments" element={<SegmentationModule />} />
          <Route path="call-list" element={<CallListModuleV2 />} />
          <Route path="appointments" element={<AppointmentModule />} />
          <Route path="physical-appointments" element={<PhysicalAppointmentModule />} />
          <Route path="follow-ups" element={<FollowUpModule />} />
          <Route path="activities" element={<LeadActivityModule />} />
          <Route path="custom-events/create" element={<CustomEventModule />} />
          <Route path="custom-events/webhook" element={<CustomEventModule />} />
          <Route path="custom-events/edit/:id" element={<CustomEventModule />} />

          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="pipeline-deal" element={<PipelineDealModule />} />
          <Route path="pipeline-history" element={<PipelineHistory />} />
          <Route path="quotations" element={<QuotationsModule />} />
          <Route path="notifications" element={<NotificationPage />} />
          <Route path="sales-target" element={<SalesTarget />} />
          {/* <Route path="/quotations" element={<QuotationListWrapper />} /> */}
          {/* Invoice Module Routes */}
          {/* <Route path="invoices" element={<InvoiceListWrapper />} /> */}
          <Route path="invoices" element={<InvoiceModule />} />
          <Route path="apps/invoice/add" element={<InvoiceAddWrapper />} />
          <Route path="apps/invoice/edit/:id" element={<InvoiceEditWrapper />} />
          <Route path="apps/invoice/preview/:id" element={<InvoicePreviewWrapper />} />
          <Route path="master-settings/invoice-settings" element={<InvoiceListSettings />} />
          <Route path="master-settings/quotation-settings" element={<QuotationListSettings />} />
          <Route path="master-settings/tax-settings" element={<TaxSettings />} />
          <Route path="master-settings/collaborator-type" element={<CollaboratorType />} />
          <Route path="master-settings/collaborator-team" element={<CollaboratorTeam />} />
          <Route path="master-settings/payment-method" element={<PaymentMethodSettings />} />
          <Route path="master-settings/followup-settings" element={<FollowUpSettings />} />
          <Route path="master-settings/pipeline-settings" element={<PipelineSettings />} />
          <Route path="master-settings/task-settings" element={<TaskSetting />} />
          <Route path="master-settings/call-dialer-settings" element={<CallDialerSetting />} />
          <Route path="master-settings/entity-approval-settings" element={<EntityApprovalSetting />} />
          <Route path="master-settings/user-hierarchy" element={<UserHierarchySettings />} />
          <Route path="master-settings/lead-assignment-master" element={<LeadAssignmentSettings />} />
          <Route path="master-settings/user-source-settings" element={<UserSourceSettings />} />
          <Route path="master-settings/campaign-settings" element={<UserCampaignSettings />} />
          <Route path="master-settings/medium-settings" element={<UserMediumSettings />} />
          <Route path="master-settings/mail-settings" element={<UserMailSettings />} />
          <Route path="master-settings/product-search-criteria" element={<ProductSearchCriteria />} />
          <Route path="master-settings/field-masking" element={<FieldMasking />} />
          <Route path="report-pages/enquiry-report" element={<EnquiryReport />} />
          <Route path="report-pages/whatsapp-subscription" element={<WhatsappSubscriptionReport />} />
          <Route path="report-pages/email-subscription" element={<EmailSubscriptionReport />} />
          <Route path="report-pages/followup-report" element={<FollowUpReport />} />
          <Route path="report-pages/usage-report" element={<UsageReport />} />
          <Route path="product/import-status" element={<ProductImportStatus />} />
          <Route path="report-pages/mass-operation-status" element={<MassOperationStatusReport />} />
          <Route path="report-pages/lead-report" element={<LeadReport />} />
          <Route path="report-pages/call-report" element={<CallReportDashboard />} />
          <Route path="report-pages/call-detail" element={<CallDetail />} />
          <Route path="revenue" element={<RevenueModule />} />
          <Route path="MergeDuplicate" element={<MergeduplicateModule />} />
          <Route path="credit-notes" element={<CreditNoteModuleWrapper />} />
          <Route path="credit-notes/settings" element={<CreditNoteSettingsPage />} />
          <Route path="settings/custom-button" element={<CustomButtonSettingPage />} />
          <Route path="lead-export" element={<LeadExportModule />} />
          <Route path="ledger" element={<CustomerLedger />} />
        </Route>
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {

  // Suppress ResizeObserver warnings globally
  useEffect(() => {
    const handler = (e) => {
      if (
        e.message === "ResizeObserver loop completed with undelivered notifications." ||
        e.message === "ResizeObserver loop limit exceeded"
      ) {
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener("error", handler);

    return () => {
      window.removeEventListener("error", handler);
    };
  }, []);

  return (
    <SSOProvider config={ssoConfig}>
      <Providers>
        <ConfigProvider theme={antdTheme}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <Suspense fallback={<div>Loading...</div>}>
                <AppRoutes />
              </Suspense>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 3000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    iconTheme: {
                      primary: '#10B981',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: '#EF4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </BrowserRouter>
            {/* React Query Devtools removed to avoid overlay in the app UI */}
          </QueryClientProvider>
        </ConfigProvider>
      </Providers>
    </SSOProvider>
  );
};

export default App;